import { createContext, useContext, useState, ReactNode } from 'react';
import { 
  TestResult, 
  Session,
  MentalReadiness,
  CardioReadiness,
  MuscularReadiness,
  StrainData,
  PerformanceTestResult
} from '../types';
import { initialTestResults, sessions as initialSessions } from '../data/mockData';
import {
  seasonMentalData,
  seasonCardioData,
  seasonMuscularData,
  seasonStrainData,
} from '../data/seasonData';
import { useTestingApi } from '../hooks/useTestingApi';
import { ITestDefinition } from '../../models/testDefinition';

export interface TestPreferences {
  strength: string[];
  speed: string[];
  endurance: string[];
  bodyComposition: string[];
  rsiTest: 'test-dropjump' | 'test-cmj';
}

export interface AthleteTestPreferences {
  [athleteId: string]: {
    rsiTest?: 'test-dropjump' | 'test-cmj';
  };
}

interface DataContextType {
  testResults: TestResult[];
  sessions: Session[];
  mentalReadiness: MentalReadiness[];
  cardioReadiness: CardioReadiness[];
  muscularReadiness: MuscularReadiness[];
  strainData: StrainData[];
  performanceTestResults: PerformanceTestResult[];
  testPreferences: TestPreferences;
  athleteTestPreferences: AthleteTestPreferences;
  athleteBenchmarks: any;
  testCatalog: ITestDefinition[];
  catalogLoading: boolean;
  teamId: string | undefined;
  addTestResult: (result: TestResult) => void;
  updateTestResult: (id: string, updates: Partial<TestResult>) => void;
  deleteTestResult: (id: string) => void;
  addSession: (session: Session) => void;
  addMentalReadiness: (data: MentalReadiness) => void;
  updateMentalReadiness: (id: string, updates: Partial<MentalReadiness>) => void;
  addCardioReadiness: (data: CardioReadiness) => void;
  updateCardioReadiness: (id: string, updates: Partial<CardioReadiness>) => void;
  addMuscularReadiness: (data: MuscularReadiness) => void;
  updateMuscularReadiness: (id: string, updates: Partial<MuscularReadiness>) => void;
  addStrainData: (data: StrainData) => void;
  updateStrainData: (id: string, updates: Partial<StrainData>) => void;
  addPerformanceTestResult: (result: PerformanceTestResult) => void;
  updatePerformanceTestResult: (id: string, updates: Partial<PerformanceTestResult>) => void;
  setTestPreferences: (preferences: TestPreferences) => void;
  setAthleteTestPreferences: (athleteId: string, preferences: { rsiTest?: 'test-dropjump' | 'test-cmj' }) => void;
}

// Default test preferences - only most important/common tests enabled by default
const defaultTestPreferences: TestPreferences = {
  strength: [
    'test-1rm-bench',
    'test-1rm-squat',
    'test-1rm-deadlift',
    'test-cmj',
    'test-pullup'
  ],
  speed: [
    'test-10m',
    'test-30m',
    'test-cmj',
    'test-yoyo'
  ],
  endurance: [
    'test-vo2max',
    'test-cooper',
    'test-yoyo',
    'test-beep'
  ],
  bodyComposition: [
    'test-weight',
    'test-bodyfat',
    'test-leanmass',
    'test-bmi'
  ],
  rsiTest: 'test-cmj'
};

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: ReactNode }) {
  const [testResults, setTestResults] = useState<TestResult[]>(initialTestResults);
  const [sessions, setSessions] = useState<Session[]>(initialSessions);
  const [mentalReadiness, setMentalReadiness] = useState<MentalReadiness[]>(seasonMentalData);
  const [cardioReadiness, setCardioReadiness] = useState<CardioReadiness[]>(seasonCardioData);
  const [muscularReadiness, setMuscularReadiness] = useState<MuscularReadiness[]>(seasonMuscularData);
  const [strainData, setStrainData] = useState<StrainData[]>(seasonStrainData);
  const [performanceTestResults, setPerformanceTestResults] = useState<PerformanceTestResult[]>([]);
  const [testPreferences, setTestPreferences] = useState<TestPreferences>(defaultTestPreferences);
  const [athleteTestPreferences, setAthleteTestPreferences] = useState<AthleteTestPreferences>({});
  const [athleteBenchmarks, setAthleteBenchmarks] = useState<any>([]);

  const { catalog, catalogLoading, teamId } = useTestingApi();

  const addTestResult = (result: TestResult) => {
    setTestResults(prev => [...prev, result]);
  };

  const updateTestResult = (id: string, updates: Partial<TestResult>) => {
    setTestResults(prev =>
      prev.map(r => (r.id === id ? { ...r, ...updates } : r))
    );
  };

  const deleteTestResult = (id: string) => {
    setTestResults(prev => prev.filter(r => r.id !== id));
  };

  const addSession = (session: Session) => {
    setSessions(prev => [...prev, session]);
  };

  const addMentalReadiness = (data: MentalReadiness) => {
    setMentalReadiness(prev => [...prev, data]);
  };

  const updateMentalReadiness = (id: string, updates: Partial<MentalReadiness>) => {
    setMentalReadiness(prev =>
      prev.map(d => (d.id === id ? { ...d, ...updates } : d))
    );
  };

  const addCardioReadiness = (data: CardioReadiness) => {
    setCardioReadiness(prev => [...prev, data]);
  };

  const updateCardioReadiness = (id: string, updates: Partial<CardioReadiness>) => {
    setCardioReadiness(prev =>
      prev.map(d => (d.id === id ? { ...d, ...updates } : d))
    );
  };

  const addMuscularReadiness = (data: MuscularReadiness) => {
    setMuscularReadiness(prev => [...prev, data]);
  };

  const updateMuscularReadiness = (id: string, updates: Partial<MuscularReadiness>) => {
    setMuscularReadiness(prev =>
      prev.map(d => (d.id === id ? { ...d, ...updates } : d))
    );
  };

  const addStrainData = (data: StrainData) => {
    setStrainData(prev => [...prev, data]);
  };

  const updateStrainData = (id: string, updates: Partial<StrainData>) => {
    setStrainData(prev =>
      prev.map(d => (d.id === id ? { ...d, ...updates } : d))
    );
  };

  const addPerformanceTestResult = (result: PerformanceTestResult) => {
    setPerformanceTestResults(prev => [...prev, result]);
  };

  const updatePerformanceTestResult = (id: string, updates: Partial<PerformanceTestResult>) => {
    setPerformanceTestResults(prev =>
      prev.map(r => (r.id === id ? { ...r, ...updates } : r))
    );
  };

  const updateAthleteTestPreferences = (athleteId: string, preferences: { rsiTest?: 'test-dropjump' | 'test-cmj' }) => {
    setAthleteTestPreferences(prev => ({
      ...prev,
      [athleteId]: {
        ...prev[athleteId],
        ...preferences
      }
    }));
  };

  return (
    <DataContext.Provider
      value={{
        testResults,
        sessions,
        mentalReadiness,
        cardioReadiness,
        muscularReadiness,
        strainData,
        performanceTestResults,
        testPreferences,
        athleteTestPreferences,
        athleteBenchmarks,
        testCatalog: catalog,
        catalogLoading,
        teamId,
        addTestResult,
        updateTestResult,
        deleteTestResult,
        addSession,
        addMentalReadiness,
        updateMentalReadiness,
        addCardioReadiness,
        updateCardioReadiness,
        addMuscularReadiness,
        updateMuscularReadiness,
        addStrainData,
        updateStrainData,
        addPerformanceTestResult,
        updatePerformanceTestResult,
        setTestPreferences,
        setAthleteTestPreferences: updateAthleteTestPreferences,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within DataProvider');
  }
  return context;
}